import { Box, Fade, Grow, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { FC, useEffect, useState } from 'react';

interface ChartItem {
  label: string;
  value: number;
}

interface Props {
  title: string;
  maxValue?: number;
  data: ChartItem[];
  loading?: boolean;
  subtitle: string;
}

const COLORS = ['#A78BFA', '#60A5FA', '#FDBA74', '#86EFAC', '#FCA5A5'];

export const AverageDayOfCompletionBar: FC<Props> = ({ title, subtitle, data, maxValue, loading = false }) => {
  const [chartReady, setChartReady] = useState(false);
  const [animatedData, setAnimatedData] = useState<ChartItem[]>([]);
  const labels = data.map((d: ChartItem) => d.label);

  const calculatedMaxValue =
    maxValue || Math.ceil(Math.max(...data.map((d) => d.value), 10) * 1.2)
  useEffect(() => {
    if (!loading) {
      setChartReady(false);
      setAnimatedData(data.map((item) => ({ ...item, value: 0 })))

      const timer = setTimeout(() => {
        setChartReady(true)
        let frame = 0
        const totalFrames = 60

        const animate = () => {
          frame++
          const progress = Math.min(frame / totalFrames, 1)
          const eased = 1 - Math.pow(1 - progress, 1)

          setAnimatedData(
            data.map((item) => ({
              ...item,
              value: item.value * eased,
            })),
          )

          if (frame < totalFrames) {
            requestAnimationFrame(animate)
          }
        }

        requestAnimationFrame(animate)
      }, 1000)

      return () => clearTimeout(timer)
    } else {
      setChartReady(false);
      setAnimatedData([]);
    }
  }, [loading, data]);
  
  const barColors = animatedData.map(
    (_, index) => COLORS[index % COLORS.length]
  );

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Title */}
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
        {title}
      </Typography>

      {/* Chart: fill remaining height */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height="75%" sx={{ borderRadius: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-around', px: 1 }}>
              {data.map((_, i) => (
                <Skeleton key={i} variant="text" width={40} height={16} />
              ))}
            </Box>
          </Box>
        ) : (
          <Fade in={chartReady} timeout={1000}>
            <Box sx={{ width: '100%', height: '200px' }}>
              <BarChart
                xAxis={[
                  {
                    scaleType: 'band',
                    data: labels,
                    tickLabelStyle: { fill: '#6B7280', fontSize: 10, fontWeight: 500, stroke: 'transparent' },
                  },
                ]}
                yAxis={[
                  {
                    max: calculatedMaxValue,
                    tickNumber: 5,
                    tickLabelStyle: { fill: '#6B7280', fontSize: 12, fontWeight: 500, stroke: 'transparent' },
                  },
                ]}
                grid={{ horizontal: true }}
                series={[
                  {
                    data: animatedData.map((d) => d.value),
                    color: 'transparent',
                  },
                ]}
                skipAnimation={true}
                sx={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  '& .MuiChartsAxis-line': { display: 'none' },
                  '& .MuiChartsAxis-domain': { display: 'none' },
                  '& .MuiChartsAxis-tick, & .MuiChartsAxis-tickLine': { stroke: 'transparent' },
                  '& .MuiChartsGrid-line': { strokeDasharray: '6 4', stroke: '#E5E7EB' },
                  ...barColors.reduce((acc, color, index) => {
                    acc[`& .MuiBarElement-root:nth-of-type(${index + 1})`] = {
                      fill: color,
                    };
                    return acc;
                  }, {} as Record<string, any>),
                
                  '& .MuiBarElement-root:hover': {
                    opacity: 0.8,
                  },
                }}
              />
            </Box>
          </Fade>
        )}
      </Box>

      {/* Legend Title */}
      <Grow
        in={chartReady && !loading && data.length > 0}
        timeout={600}
        style={{ transitionDelay: '800ms' }}
      >
        <Typography variant="subtitle2" sx={{mb: -1, fontSize: 12, justifyContent: 'center', display: 'flex', color: '#6B7280' }}>
          {subtitle}
        </Typography>
      </Grow>

      {/* Legend */}
      <Stack spacing={1} sx={{ mt: 2 }}>
        {loading ? (
          // Skeleton for legend
          data.map((_, index) => (
            <Box
              key={`skeleton-${index}`}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 5, pl: 5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={60} height={16} />
              </Box>
              <Skeleton variant="text" width={30} height={16} />
            </Box>
          ))
        ) : (
          data.map((item: ChartItem, index: number) => (
            <Grow
              key={item.label}
              in={chartReady && !loading}
              timeout={600}
              style={{ transitionDelay: `${index * 150 + 800}ms` }}
            >
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 5, pl: 5 }}
            >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: '1rem',
                  height: '1rem',
                  borderRadius: '50%',
                  backgroundColor: COLORS[index % COLORS.length],
                }}
              />
              <Typography variant="body2" sx={{ color: '#6B7280', fontSize: 12, fontWeight: 500 }}>
                {item.label}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: 12, fontWeight: 500 }}>
              {item.value !== undefined ? `${item.value}` : '0'}
            </Typography>
          </Box>
            </Grow>
          ))
        )}
      </Stack>
    </Box>
  );
};
